import React, { useState, useRef } from "react";
import { useWindowMax } from "../hooks/useWindowMax"; // 193 §D — the te window's measure, shared
import { BanknoteArrowDown } from "lucide-react";
import { fc, fd, firmCurrencyOf } from "../utils";
import { recordInvoicePayment, deleteInvoicePayment } from "../lib/invoiceFetch";
import { invoiceAmountDue, isOverduePF, isOwed, OWED_TOLERANCE } from "../lib/clientMatters.mjs"; // 191 E.2f — one overdue; E.2g — the half-cent rule
import { useTranslation } from "../i18n";
import { useConfirm } from "./useConfirmAndUndo";
import type { ToastFn } from "./Toast"; // 216 §3 (2) — the host's toast, read through its own type once
import { RAD, FS } from "../lib/tokens";
import { ModalLayout, HScroll, SortCaret } from "./SharedUI"; // 191 E.2e — the strip's own scroller, with its arrows; 200-glance §M — the one sort caret
import Table from "./Table";
import AmountField from "./AmountField"; // 208 §10 — the one amount field

/* 158 §5 — ONE INVOICE TABLE.

   THE BRIEF'S DIRECTION WAS INVERTED, and the census says so plainly: this
   file was ALREADY the shared component, mounted by CaseView and
   CollectiveCaseView and by nothing else, while the Invoices section rendered
   its own table INLINE inside `InvoiceView.tsx` and exported no component to
   swap in. The hand-rolled table was therefore the one the brief pointed AT,
   and the "Invoices section's own component" did not exist.

   So the move ran the other way: the Invoices section's table came HERE, whole,
   and the older implementation this file used to hold is gone. What it used to
   show, and what the recap beside it therefore contradicted, was a STATUS WORD
   — "Partiel" — where the Invoices section showed the two figures a firm
   actually reads: PAYÉ and RESTANT. Same rows, same served fields, two
   different answers; now one.

   Everything below is the Invoices section's code, moved and not improved:
   the sortable headers, the sort state, the send-channel chips, the row click,
   the payment button and the payment modal. Two things did NOT survive the
   move, both dead where they stood: `getInvoicePaymentStatusMeta`, whose `st`
   had no reader left after Phase 35 Stage-3 r2 replaced the status column with
   the To Client / To Peppol chips, and the old table's own `totalPaid`, which
   it computed and never rendered.

   `scope` is the same flag InvoiceView always used: truthy hides the CASE and
   TITLE columns because the host already names the matter. CV and CCV are
   scoped by construction and pass it; the Invoices section passes its own. */

// 191 E.2b — the ladder cells share one style (the fees cell's own, as it
// stood), and a served figure that is NULL reads "—": §4.2's law, absent is
// never zero (PF-2026/009 and /010 store no ladder at all).
const LADDER_CELL = {
  fontSize: FS.s10,
  padding: "6px",
  textAlign: "right" as const,
  fontFamily: "'JetBrains Mono',monospace",
};
const amt = (v: number | null | undefined, currency?: string | null) =>
  v == null ? "—" : fc(v, currency);
// The ladder's read-set, typed once at the seam: a row is read into this
// shape ONCE (the ratchet counts every property read off `any`).
interface LadderRow {
  feeTotal?: number | null;
  costsAmount?: number | null;
  costsRatio?: number | null;
  commercialDeduction?: number | null;
  subtotalExVAT?: number | null;
  totalInclVAT?: number | null;
  retainerDeduction?: number | null;
  totalToPay?: number | null;
  dueDate?: string | null;
  currency?: string | null;
  docKind?: string | null;
}

// BUG-A SWEEP — hoisted out of the render body. It was declared inside it and
// mounted as JSX, so every render gave React a new component type for each
// sortable column header and rebuilt all six <th> elements instead of updating
// them. The two helpers it closed over — cycleSort and sortInd — arrive as
// props; that is what makes the move possible without changing its behaviour.
const TH = ({ k, label, right, cycleSort, sortInd }: any) => (
  <th
    style={{
      fontSize: FS.s9,
      padding: "5px 6px",
      textAlign: right ? "right" : "left",
      color: "var(--tm)",
      cursor: "pointer",
      userSelect: "none",
      whiteSpace: "nowrap",
    }}
    onClick={() => cycleSort(k)}
  >
    {label}
    {sortInd(k)}
  </th>
);

// Phase 35 Stage-3 — Overdue = unpaid-ish AND past due date, no grace. 191
// E.2f: ONE definition now, `isOverduePF` in src/lib/clientMatters.mjs (the
// Invoices section's filter counts read the same export); this table calls it
// per row with the default clock, as its own copy did.
// Phase 35 (C3) — a pro forma's credit adjustments reduce the effective
// total. It came with the payment modal, its only consumer.
const sumCredits = (i: any) =>
  (i.credits || []).reduce((s3: any, c: any) => s3 + Number(c.amount || 0), 0);

// 160 §1 — the shape the removal path actually touches, named rather than
// `any`. `shared/entities`' Invoice types `payments` as `JsonValue | null`,
// which every reader here would have to narrow at each access; the rest of this
// file predates that type and is `any` throughout, so a narrow local shape is
// what an addition can honestly carry. `id` on an entry is 160 §1's own field:
// the served entry carries its Payment ROW id when the row store answered
// (server/src/lib/shapes.js structuralShapeInvoice), and is absent when it did
// not — which is the × 's whole gate.
type PaymentEntry = {
  id?: number | null;
  amount: number;
  date: string;
  method?: string | null;
  reference?: string | null;
};
type ServedInvoice = {
  /** 169 R4 — the document's currency, chosen at issue (ISO 4217; EUR on every pre-169 row). */
  currency?: string | null;
  id?: number | null;
  sentToClientAt?: string | null;
  sentToPeppolAt?: string | null;
  payments?: PaymentEntry[] | null;
  /** 216 §3 — read once after a record, for the toast's word (widened, not cast). */
  paymentStatus?: string | null;
};
/* 216 addendum 3 §3′ (2) [ADVISOR] — ONE QUESTION, « IS SOMETHING DUE? », ASKED
   AS THE LIST ASKS IT. The window's own « due » was browser arithmetic
   (totalToPay − credits − Σ payments) tested with a plain `> 0` three times —
   the record button, the « Dû » colour, the recording part — while the list's
   DÛ cell reads the SERVED figure (`invoiceAmountDue`) under the half-cent rule
   (`isOwed`, 191 E.2g). Laurent's PF-2026/057 (12/10) is the gap: a total
   carrying a sub-cent residue, paid to the cent below it, read « Dû 0,00 € » in
   red with a recording part pre-filled « 0,00 », and the removal that followed
   left the window asking for an amount. So: THE FIGURE is the served due; THE
   RULE is the list's for a positive document, and mirrored for a reimbursement —
   a negative document (161 §6) whose repayments are negative payments: something
   is due while its due is below minus half a cent, as the server's status reads
   its sign (lib/paymentRows.js computeQuartetFromRows). THE PRE-FILL is the due
   rounded to the cent, its sign kept. One function, read at the opening, in the
   window and after a removal. */
type DueDoc = {
  totalToPay?: number | null;
  amountDue?: number | null;
  credits?: unknown;
  payments?: unknown;
};
type DueState = { due: number; owed: boolean; prefill: string };
const dueStateOf = (doc: DueDoc | null | undefined): DueState => {
  const due = invoiceAmountDue(doc);
  const negative = Number(doc?.totalToPay ?? 0) < 0;
  const owed = negative ? due < -OWED_TOLERANCE : isOwed(due);
  return { due, owed, prefill: (Math.round(due * 100) / 100).toFixed(2) };
};
/** 216 §3 — the record is already done when the undo window opens; nothing to confirm. */
const recordedStands = () => {
  // the act stands; only its reversal is offered (confirmAndUndo with no label and no message)
};
/** What Undo re-records: the payment, verbatim. The date string is carried
 *  as it was stored, so a bare date stays bare and an ISO stamp stays one. */
type PaymentSnapshot = {
  invoiceId: number;
  amount: number;
  date: string;
  reference: string;
  method: string | null;
  /** 216 §3 (3) — the slot the payment held: the undo puts it back there. */
  sortOrder: number;
};

export default function InvoiceTable({
  invoices,          // the rows to draw, already filtered by the host
  scope = null,      // truthy → the host names the matter; hide CASE + TITLE
  emptyHint = null,  // host-written tail of the "no invoices" line, if any
  /* 158 §5 — the FACTURES caption the matter views have always drawn above
     this table. The Invoices section is a whole screen and needs no caption;
     inside a matter's financial recap the table is one panel among several
     and does, so the label rides with the mount rather than being assumed
     either way. */
  labelled = false,
  inv,               // the viewer's whole list, for the payment modal's target
  sInv,
  toast,
  onViewInvoice,
  /* 209 §14 [RULED 19] — `firmSettings` STOOD HERE: this table read ONE flag
     off it, the approval switch (179 B.6), which is gone — the three hosts no
     longer pass it. */
  /* 193 §D — the Invoices screen passes true: the table becomes a WINDOW on
     the te pattern (a measured max-height, `overflow: auto`, the header
     pinned at its top, the four scroll hints), which is the only shape in
     which a sticky header can pin here — its 18 columns scroll inside
     `HScroll`, an `overflow-x: auto` box, and such a box is the nearest
     scroll container of everything in it (182 D.5); a header pinned against
     it scrolls away with the page unless the box IS the page's window.
     Measured before anything was written (193 D.2): at firm scope the header
     sat at −1064 with the last row at 537; inside a case, the te window
     scrolled to its end, the te header had left with its table (−143, its
     table's bottom −119) and the invoice header sat at −81 — no two headers
     ever overlapped, and neither pinned. CaseView and CollectiveCaseView pass
     nothing and render byte for byte what they did. */
  windowed = false,
}: any) {
  // 179 B.6 — `useTranslation()` hands back the context, which is typed `any`
  // (i18n.tsx, `createContext<any>`), so every `t()` call in this file counted
  // as an unsafe call under the eslint ratchet and any NEW word would have
  // raised the file. Narrowed to the function's real contract at the one
  // place it is taken; nothing at runtime changes.
  const { t } = useTranslation() as { t: (key: string, vars?: Record<string, unknown>) => string };
  // 160 §1 — THE APP'S ONE CONFIRM, off the context (157 §2). None of this
  // table's three hosts — InvoiceView, CaseView, CCV — hands it a
  // `confirmAndUndo` prop, and drilling one through three trees to reach a
  // dialog App already renders would be a fourth mechanism, not a reuse.
  const askConfirm = useConfirm();
  const say = toast as ToastFn; // 216 §3 — the host's toast, read through its own type once
  const [sort, setSort] = useState({ k: "date", d: -1 });
  const [payMod, setPayMod] = useState<any>(null);
  /* 193 §D — the window's box and its measure (hooks/useWindowMax.ts, the
     te's own arithmetic): the section is the host's `.invBlock` (InvoiceView
     wraps the controls row and this table in it), so the controls stand still
     and the rows scroll, exactly as the te's title and filters do. */
  const winRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const { winMax, hintInsets } = useWindowMax({ enabled: windowed === true, win: winRef, table: tableRef, sectionSelector: ".invBlock" });

  const sorted = [...(invoices || [])].sort((a, b) => {
    const d = sort.d,
      k = sort.k;
    if (k === "number")
      return d * (a.number || "").localeCompare(b.number || "");
    if (k === "date")
      return d * (a.invoiceDate || "").localeCompare(b.invoiceDate || "");
    if (k === "case")
      return d * (a.caseRef || "").localeCompare(b.caseRef || "");
    if (k === "client")
      return d * (a.clientName || "").localeCompare(b.clientName || "");
    if (k === "fees") return d * ((a.feeTotal || 0) - (b.feeTotal || 0));
    /* 191 E.2b — the ladder's columns sort like their neighbours: each on
       the served field its cell reads (the two rows read once, typed). */
    const A = a as LadderRow;
    const B = b as LadderRow;
    if (k === "costs") return d * ((A.costsAmount ?? 0) - (B.costsAmount ?? 0));
    if (k === "discount") return d * ((A.commercialDeduction ?? 0) - (B.commercialDeduction ?? 0));
    if (k === "subtotal") return d * ((A.subtotalExVAT ?? 0) - (B.subtotalExVAT ?? 0));
    if (k === "totalIncl") return d * ((A.totalInclVAT ?? 0) - (B.totalInclVAT ?? 0));
    if (k === "retainer") return d * ((A.retainerDeduction ?? 0) - (B.retainerDeduction ?? 0));
    if (k === "total") return d * ((a.totalToPay || 0) - (b.totalToPay || 0));
    if (k === "dueDate")
      return d * (A.dueDate ?? "").localeCompare(B.dueDate ?? "");
    if (k === "paid") {
      const ap = (a.payments || []).reduce((s2: any, p: any) => s2 + p.amount, 0);
      const bp = (b.payments || []).reduce((s2: any, p: any) => s2 + p.amount, 0);
      return d * (ap - bp);
    }
    if (k === "due") return d * (invoiceAmountDue(a) - invoiceAmountDue(b));
    if (k === "status")
      return d * (a.paymentStatus || "").localeCompare(b.paymentStatus || "");
    return 0;
  });

  const cycleSort = (k: any) => {
    if (sort.k === k) setSort({ k, d: sort.d === 1 ? -1 : 1 });
    else setSort({ k, d: 1 });
  };
  const sortInd = (k: any) => <SortCaret active={sort.k === k} d={sort.d} />; // 200-glance §M — the one sort caret

  // Phase 35 Stage-3 (item 9) — record through the dedicated
  // POST /:id/payments endpoint, NOT the generic PUT (which is lock-gated and
  // would 409 on an active/sent pro forma). The server recomputes
  // paidAmount/balance/status and returns the shaped invoice.
  // The one place a reshaped invoice lands in the viewer's list. Three callers
  // now (record, remove, and the Undo that re-records), so it is written once.
  const applyInvoice = (invoice: ServedInvoice | null | undefined) => {
    if (!invoice || invoice.id == null) return;
    sInv((arr: any) =>
      (arr || []).map((i: any) => (i.id === invoice.id ? invoice : i)),
    );
  };
  /* 216 addendum 3 §3′ (1) [RULED 78] — AFTER A REMOVAL THE WINDOW STAYS OPEN,
     UPDATED. Its summary line and its list already read the viewer's row
     (`target` is found in `inv` on every render, and applyInvoice just
     replaced it); what did not follow was the amount offered for recording,
     which kept the figure the window was opened with (« 0,00 » on Laurent's
     paid pro forma — hence « Entrez un montant valide »). The amount becomes
     what is due NOW (dueStateOf's pre-fill); the date and the reference keep
     what the window holds. The same when the toast's « Annuler » puts the
     payment back while the window is open: the window follows the served
     document (nothing due → the recording part hides, §3′ (2)). Guarded on the
     window still being open on THAT document: an undo can fire after the
     window closed, or after another was opened. */
  const followServed = (invoice: ServedInvoice | null | undefined) => {
    if (!invoice || invoice.id == null) return;
    // `fill` counts the fills: the amount field is keyed on it, so a figure the
    // host sets is SHOWN — the field keeps what was typed while it has focus
    // (207C §2's rule, AmountField's `editing`), and after a removal it may
    // still have it (WebKit does not move focus to a clicked button); re-mounted,
    // it starts from the new figure and takes the focus again (`autoFocus`).
    setPayMod((p: { invoiceId: number; fill?: number } | null) =>
      p && p.invoiceId === invoice.id ? { ...p, amount: dueStateOf(invoice as DueDoc).prefill, fill: (p.fill ?? 0) + 1 } : p,
    );
  };

  const recordPayment = async () => {
    // 161 §6 — a NEGATIVE amount is the repayment leg of a reimbursement and
    // is legal ONLY when the target document is a credit (the server
    // enforces the same rule, 400 coded). Zero never records.
    const payTarget = payMod
      ? ((invoices || []).find((i: { id: number }) => i.id === payMod.invoiceId)
        || (inv || []).find((i: { id: number }) => i.id === payMod.invoiceId) || null)
      : null;
    const allowNegative = payTarget?.docKind === "credit";
    const amtNum = payMod ? +payMod.amount : NaN;
    // 216 addendum 6 §10 (4) [62 (a)] — and the mirror: a positive amount on a
    // reimbursement is a mistake, refused here before sending and by the
    // server (400 invoice.positive_payment_on_refund), as a negative one is
    // off a reimbursement.
    if (!payMod || !Number.isFinite(amtNum) || amtNum === 0 || (amtNum < 0 && !allowNegative) || (amtNum > 0 && allowNegative)) {
      // 216 addendum 3 §3′ (5) [ADVISOR] — A REFUSAL LOOKS LIKE A REFUSAL: this
      // one passed no kind and showed as a success (« ✓ Entrez un montant
      // valide », Laurent's screenshot of 12/10); it takes the error kind and
      // the shape of this file's other refusals.
      toast(`⚠ ${t("invoice.toast.enter_valid_amount")}`, "error");
      return;
    }
    const amt = Math.round(parseFloat(payMod.amount) * 100) / 100;
    const result = await recordInvoicePayment(payMod.invoiceId, {
      amount: amt,
      date: payMod.date,
      reference: payMod.reference || "",
    });
    if (!result.ok) {
      toast(`⚠ ${result.error || t("invoice.payment_failed", { fallback: "Payment failed." })}`, "error");
      return;
    }
    applyInvoice(result.invoice);
    setPayMod(null);
    // 216 §3 — the served document, read once through its type (the ratchet).
    const served = (result.invoice ?? null) as ServedInvoice | null;
    const paidWord = served?.paymentStatus === "paid"
      ? t("invoice.toast.fully_paid")
      : t("invoice.toast.partial_payment");
    // 216 §3 [RULED 72; ADVISOR — his « ALWAYS »] — RIGHT AFTER RECORDING, THE
    // TOAST OFFERS « ANNULER » through the house's undo window (144 §5): the
    // payment just recorded is the last entry served, and the undo removes it
    // through the same DELETE the × takes. No dialog (no label, no message):
    // the act is already done, only its reversal is offered.
    const recorded = served?.payments ?? [];
    const last = recorded.length ? recorded[recorded.length - 1] : null;
    if (last?.id == null) {
      say(paidWord);
      return;
    }
    const recordedId = last.id;
    const onInvoice = (payMod as { invoiceId: number }).invoiceId;
    askConfirm({
      onConfirm: recordedStands,
      undo: {
        snapshot: { invoiceId: onInvoice, paymentId: recordedId },
        restore: (snap: { invoiceId: number; paymentId: number }) => {
          void deleteInvoicePayment(snap.invoiceId, snap.paymentId).then((back) => {
            if (!back.ok) {
              say(`⚠ ${back.error ?? t("invoice.payment.remove_failed", { fallback: "The payment could not be removed." })}`, "error");
              return;
            }
            applyInvoice((back.invoice ?? null) as ServedInvoice | null);
          });
        },
      },
      toastName: paidWord,
    });
  };

  // 160 §1 — A PAYMENT ON AN UNSENT PRO FORMA CAN BE TAKEN BACK. House removal
  // shape (144 §5): confirm, then a 5s Undo window. The confirm's TITLE carries
  // the data — the amount and the date — which is 140 §2's rule for a dialog
  // that must name the row it is about; no `message` is passed, so the dialog
  // keeps its destructive arm (a red button, and the body that states the undo
  // window) rather than the forward-action one.
  //
  // Undo re-RECORDS the same {amount, date, reference, method} through the
  // record route. The money comes back exactly (the date string is passed
  // verbatim, so a bare date stays bare and an ISO stamp stays an ISO stamp)
  // — and, 216 §3 (3) [ADVISOR], at the SLOT it held: `sortOrder` rides the
  // snapshot and the record route splices the entry in there (an insert at a
  // slot is the one server path; until 216 the undo appended, so a payment
  // removed from the middle returned last).
  const removePayment = (target: ServedInvoice, p: PaymentEntry, at: number) => {
    // Both ids are what the route is addressed by, and the × only renders when
    // the entry has one. Stated rather than asserted: a removal with a missing
    // id would be a DELETE against `/undefined`.
    if (target.id == null || p.id == null) return;
    const invoiceId = target.id;
    const paymentId = p.id;
    // 169 R8 — a payment prints in its document's currency (a literal € stood here).
    const money = fc(p.amount, target.currency);
    const label = `${money} · ${fd(p.date)}`;
    askConfirm({
      label: t("invoice.payment.one", { fallback: "this payment" }),
      title: t("invoice.payment.remove_title", {
        amount: money,
        date: fd(p.date),
        fallback: `Remove the payment of ${money} of ${fd(p.date)}?`,
      }),
      onConfirm: async () => {
        const result = await deleteInvoicePayment(invoiceId, paymentId);
        if (!result.ok) {
          toast(`⚠ ${result.error || t("invoice.payment.remove_failed", { fallback: "The payment could not be removed." })}`, "error");
          return;
        }
        const served = (result.invoice ?? null) as ServedInvoice | null; // read once through its type (the ratchet)
        applyInvoice(served);
        followServed(served); // 216 addendum 3 §3′ (1) [RULED 78] — the window stays, its amount what is due now
        // 216 addendum 3 §3′ (3) [RULED 78] — a payment removed in this window: the button that leaves reads « Fermer »
        setPayMod((p: { invoiceId: number } | null) => (p && p.invoiceId === invoiceId ? { ...p, removed: true } : p));
      },
      undo: {
        snapshot: { invoiceId, amount: p.amount, date: p.date, reference: p.reference || "", method: p.method ?? null, sortOrder: at },
        restore: async (snap: PaymentSnapshot) => {
          const result = await recordInvoicePayment(snap.invoiceId, {
            amount: snap.amount, date: snap.date, reference: snap.reference, method: snap.method, sortOrder: snap.sortOrder,
          });
          if (!result.ok) {
            toast(`⚠ ${result.error || t("invoice.payment.restore_failed", { fallback: "The payment could not be put back." })}`, "error");
            return;
          }
          const served = (result.invoice ?? null) as ServedInvoice | null;
          applyInvoice(served);
          followServed(served); // 216 addendum 3 §3′ (1) — the window, if still open, follows the served document
        },
      },
      toastName: t("invoice.payment.removed_toast", {
        payment: label,
        fallback: `Payment removed — ${label}`,
      }),
    });
  };

  return (
    <>
      {labelled && (
        <div
          style={{
            fontSize: FS.s10,
            fontWeight: 600,
            color: "var(--tm)",
            textTransform: "uppercase",
            letterSpacing: ".3px",
            marginTop: 14,
            marginBottom: 6,
          }}
        >
          {t("invoiceTable.invoices", { fallback: "Invoices" })}
        </div>
      )}
      {/* 191 E.2e — eighteen columns on the Invoices section overflow: the
          table takes `HScroll` (the 14-day strip's wrapper with its arrows,
          the matter lists' since 190 round 7 [AH], any overflowing table's by
          190 round 6 [X]) inside `.table-wrap`, the wrapper itself clipping
          (`elist-wrap`, 182 D.5's idiom) so there is ONE scroller and the
          hints measure it. Copied, not invented. */}
      <div className="table-wrap elist-wrap">
        <HScroll
          {...(windowed
            ? {
                /* 151D §1(f) — measured (the hook above); the calc is only the
                   first paint's stand-in until the measurement lands (the te
                   window's own line, TimeEntryTable.tsx). */
                maxHeight: winMax ?? "max(240px, calc(100dvh / var(--ui-zoom, 1) - 270px))",
                scrollerRef: winRef,
                hintInsets,
              }
            : {})}
        >
        <Table ref={tableRef} className={windowed ? "invTable" : undefined} style={{ width: "100%" }}>
          <thead>
            <tr>
              <TH k="number" label={t("invoice.col.number")} cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="date" label={t("invoice.col.date")} cycleSort={cycleSort} sortInd={sortInd} />
              {!scope && <TH k="case" label={t("invoice.col.case")} cycleSort={cycleSort} sortInd={sortInd} />}
              {!scope && (
                <th
                  style={{
                    fontSize: FS.s9,
                    padding: "5px 6px",
                    color: "var(--tm)",
                  }}
                >
                  {t("invoice.col.title")}
                </th>
              )}
              <th
                style={{ fontSize: FS.s9, padding: "5px 6px", color: "var(--tm)" }}
              >
                {t("invoice.col.client")}
              </th>
              <th
                style={{ fontSize: FS.s9, padding: "5px 6px", color: "var(--tm)" }}
              >
                {t("invoice.col.period")}
              </th>
              {/* 191 E.0 [RULED, LAURENT] — THE PRO FORMA'S OWN LADDER, TEN
                  COLUMNS, as the modal shows it (ProFormaModal.tsx): Honoraires ·
                  Frais (with the document's %) · Remise · Sous-total HTVA ·
                  Total TVAC · Déduction de provision · Total à payer · Échéance
                  · Payé · Dû. Honoraires · Total · Payé · Solde stood here
                  (`invoice.col.total` and `invoice.col.balance` lose their only
                  reader → the 191 retired-rows one-shot). Every header is an
                  existing key but the discount's, seeded by 191; each cell
                  reads its served field; the paid cell keeps 159 §4/§5's rule
                  and its green, the due cell [BB]'s colour. */}
              <TH k="fees" label={t("invoice.col.fees")} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="costs" label={t("proforma.costs", { fallback: "Costs" })} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="discount" label={t("invoice.col.commercial_deduction", { fallback: "Discount" })} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="subtotal" label={t("proforma.subtotal_ex_vat", { fallback: "Subtotal ex. VAT" })} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="totalIncl" label={t("cc.terms.total_vatincl", { fallback: "Total VAT Incl." })} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="retainer" label={t("proforma.retainer_deduction", { fallback: "Retainer deduction" })} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="total" label={t("proforma.total_to_pay", { fallback: "Total to pay" })} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="dueDate" label={t("field.due_date", { fallback: "Due Date" })} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="paid" label={t("invoice.col.paid")} right cycleSort={cycleSort} sortInd={sortInd} />
              <TH k="due" label={t("invoice.summary.due", { fallback: "Due" })} right cycleSort={cycleSort} sortInd={sortInd} />
              {/* 191 E.2c — "To Client / To Peppol" is headed STATUT: the chips
                  below keep their own two keys; the header reads the one that
                  had no reader. */}
              <th style={{ fontSize: FS.s9, padding: "5px 6px", color: "var(--tm)", whiteSpace: "nowrap" }}>
                {t("invoice.col.status", { fallback: "Status" })}
              </th>
              <th
                style={{ fontSize: FS.s9, padding: "5px 6px", color: "var(--tm)" }}
              ></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td
                  colSpan={scope ? 16 : 18}
                  style={{
                    textAlign: "center",
                    padding: 20,
                    fontSize: FS.s11,
                    color: "var(--tm)",
                  }}
                >
                  {t("invoice.list.empty")}
                  {/* 158 §5 — the suffix names the HOST's filters ("…with
                      this status", "…matching q"), so the host writes it: the
                      Invoices section still says exactly what it said, and a
                      matter view that has no such filters says nothing. */}
                  {emptyHint}
                </td>
              </tr>
            )}
            {sorted.map((iv) => {
              const isCancelled = iv.paymentStatus === "cancelled";
              // 179 B.6 — read once through a typed view of the row (the ratchet).
              const approved = !!(iv as { approvedDate?: string | null }).approvedDate;
              // 209 §15 — sent while unapproved (the approve route's own test): never approvable.
              const sentRow = iv as { sentToClientAt?: string | null; sentToPeppolAt?: string | null; billitSubmittedAt?: string | null };
              // 216 §3 [RULED 72] — a paid document that carries a payment keeps its payment window (to take one back)
              const carriesPayment = ((iv as ServedInvoice).payments ?? []).length > 0;
              const sentUnapproved = !approved && [sentRow.sentToClientAt, sentRow.sentToPeppolAt, sentRow.billitSubmittedAt].some(Boolean);
              let approvalWord = t("invoice.approval.pending", { fallback: "Approval" });
              if (approved) approvalWord = t("invoice.approval.approved", { fallback: "Approved" });
              else if (sentUnapproved) approvalWord = t("invoice.approval.sent_unapproved", { fallback: "Sent without approval" });
              /* 159 §4/§5 — ONE RULE FOR PAYÉ, SHARED WITH THE STRIP
                 (TimeEntryTable's `paidOf`): allocations for a live document,
                 rows-still-held for a cancelled one, the JSON only while
                 neither figure was served. Reading the JSON here and
                 allocations there is how one row came to say paid 0,00 € beside
                 a RESTANT that had just dropped — the same contradiction 158 §5
                 collapsed between a status WORD and two figures.
                 `iv.paidHeld` is 0 once a cancel has re-homed the money to the
                 matter, which is where it then shows. */
              const jsonPaid = (iv.payments || []).reduce(
                (s2: any, p: any) => s2 + p.amount,
                0,
              );
              const totalPaid = isCancelled
                ? (iv.paidHeld == null ? jsonPaid : iv.paidHeld)
                : (iv.paidAllocated == null ? jsonPaid : iv.paidAllocated);
              // P17.1c — credit-aware per-invoice due (server figure; falls back
              // to totalToPay − credits − payments for boot-cache rows).
              const remaining = invoiceAmountDue(iv);
              // 153 §2 — the trashed arm of this style is gone with the
              // filter: this list only ever held live rows. A trashed row is
              // still openable BY ID (`GET /:id` returns it) and ProFormaModal
              // still offers Restore in its footer — that path is untouched.
              /* 158 §2 CORRECTED — OPACITY WAS THE WRONG TOOL, AND COLOUR IS
                 THE RIGHT ONE. Cancelling voids the fees, the total and the
                 remainder; it does not un-receive the money, and a
                 struck-through, faded 1 400,00 € says the firm was never paid
                 it (§1's defect again, in the list this time).

                 The row used to carry `opacity: 0.4`, which is a COMPOSITING
                 instruction: the whole `<tr>` becomes one layer and no
                 descendant can render at more than 0.4 of it. That is why my
                 first attempt could free the amount from the strike-through
                 and not from the fade — and why moving the rule onto nine
                 `<td>`s would have been the wrong repair anyway.

                 COLOUR COMPOSES; OPACITY DOES NOT. The void row now takes the
                 app's own muted-text token, `var(--tm)` — the same value
                 every de-emphasised figure in this tree already uses —
                 keeping the line-through. A child can then simply declare a
                 different colour and win. MEASURED against `--bc`, the
                 surface these rows sit on: --tm reads 2.77 : 1 on the dark
                 theme, 2.11 : 1 on the light and 4.83 : 1 on blue — quieter
                 than the row's normal `--ts`, and legible in all three.

                 The paid cell then overrides in full: plain green, bold, no
                 strike, at FULL STRENGTH, because there is no longer a layer
                 above it holding it down. `textDecoration` still propagates
                 into in-flow descendants and cannot be switched off by one —
                 except into an ATOMIC INLINE-LEVEL box, so the figure keeps
                 its `inline-block` wrapper. */
              const rowStyle = isCancelled
                ? { color: "var(--tm)", textDecoration: "line-through" }
                : { cursor: "pointer" };
              // 191 E.2b — the ladder's read-set, read once (LadderRow); an
              // activities document carries the costs line, a retainer or a
              // credit does not (ProFormaModal.tsx 161C §1 names them).
              const L = iv as LadderRow;
              const ladderDoc = !L.docKind || L.docKind === "activities";
              // [BB] — the DÛ figure's colour: red only when overdue (the picker's rule), muted on the "—", the row's own otherwise
              // 191 E.2g — "anything owed" is half a cent (isOwed, named once): PF-2026/008's 0.0025 reads "—"
              // 216 addendum 6 §10 (4) [62 (a)] — asked as the WINDOW asks it since addendum 3 (dueStateOf): a
              // reimbursement by its sign. The positive rule alone read « — » for every reimbursement, whatever
              // it still had to give back (measured, the deploy note §7.6); its due shows now, and « — » once
              // it is repaid, as a paid document reads.
              const outstanding = dueStateOf(iv as DueDoc).owed && !isCancelled;
              let dueColor: string | null = null;
              if (!outstanding) dueColor = "var(--tm)";
              else if (isOverduePF(iv)) dueColor = "var(--rd)";
              const unstruck = totalPaid > 0 && isCancelled
                ? {
                    display: "inline-block",
                    textDecoration: "none",
                    color: "var(--gn)",
                    fontWeight: 600,
                  }
                : undefined;
              return (
                <tr
                  key={iv.id}
                  style={rowStyle}
                  onClick={() => onViewInvoice && onViewInvoice(iv.id)}
                >
                  <td
                    style={{
                      fontSize: FS.s10_5,
                      padding: "6px",
                      fontFamily: "'JetBrains Mono',monospace",
                      color: "var(--ac)",
                      fontWeight: 600,
                    }}
                  >
                    {iv.number}
                  </td>
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "6px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fd(iv.invoiceDate)}
                  </td>
                  {!scope && (
                    <td
                      style={{
                        fontSize: FS.s10,
                        padding: "6px",
                        fontFamily: "'JetBrains Mono',monospace",
                        color: "var(--ts)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {iv.caseRef || "—"}
                    </td>
                  )}
                  {!scope && (
                    <td
                      style={{
                        fontSize: FS.s10,
                        padding: "6px",
                        maxWidth: 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={iv.caseTitle}
                    >
                      {iv.caseTitle || "—"}
                    </td>
                  )}
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "6px",
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {iv.clientName || "—"}
                  </td>
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "6px",
                      color: "var(--tm)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {/* 161 §4 — a retainer carries no period; the cell names
                        the document kind instead. (Measured: this column has
                        no TH k= sorter — a plain <th> — so there is no sort
                        to keep working.) */}
                    {iv.docKind === "retainer"
                      ? t("invoice.kind.retainer", { fallback: "Retainer" })
                      : iv.docKind === "credit"
                        ? t("invoice.kind.credit", { fallback: "Credit" })
                        : (iv.periodDisplay || "—")}
                  </td>
                  <td style={LADDER_CELL}>
                    {fc(iv.feeTotal, iv.currency)}
                  </td>
                  {/* Frais — the amount with the document's own costs ratio in
                      muted small type; a retainer or a credit carries no costs
                      line in the modal (161C §1) and reads "—" here. */}
                  <td style={LADDER_CELL}>
                    {ladderDoc
                      ? (
                        <>
                          {amt(L.costsAmount, L.currency)}
                          {L.costsRatio != null && (
                            <span style={{ fontSize: FS.s8_5, color: "var(--tm)", marginLeft: 4 }}>
                              ({L.costsRatio} %)
                            </span>
                          )}
                        </>
                      )
                      : "—"}
                  </td>
                  {/* Remise — a deduction reads with its sign, as the modal's
                      line does; the row's own colour ([BB] ruled two colours,
                      PAYÉ and DÛ, and no third). */}
                  <td style={LADDER_CELL}>
                    {(L.commercialDeduction ?? 0) > 0 ? `−${fc(L.commercialDeduction, L.currency)}` : "—"}
                  </td>
                  <td style={LADDER_CELL}>
                    {amt(L.subtotalExVAT, L.currency)}
                  </td>
                  {/* Total TVAC — served (191 E.2a), never computed here. */}
                  <td style={LADDER_CELL}>
                    {amt(L.totalInclVAT, L.currency)}
                  </td>
                  <td style={LADDER_CELL}>
                    {(L.retainerDeduction ?? 0) > 0 ? `−${fc(L.retainerDeduction, L.currency)}` : "—"}
                  </td>
                  <td style={{ ...LADDER_CELL, fontWeight: 600 }}>
                    {fc(iv.totalToPay, iv.currency)}
                  </td>
                  <td style={{ ...LADDER_CELL, fontFamily: undefined, whiteSpace: "nowrap" }}>
                    {L.dueDate ? fd(L.dueDate) : "—"}
                  </td>
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "6px",
                      textAlign: "right",
                      fontFamily: "'JetBrains Mono',monospace",
                      color: totalPaid > 0 ? "var(--gn)" : "var(--tm)",
                    }}
                  >
                    <span style={unstruck}>
                      {totalPaid > 0 ? fc(totalPaid, iv.currency) : "—"}
                    </span>
                  </td>
                  {/* 190 round 11 [BB] (ruled) — "Paid column in green; Due column
                      red if overdue, like in the picker." The paid cell above is
                      green wherever money was received (`totalPaid > 0`, since
                      158 §2) and stays so. The DUE figure was red whenever anything
                      was outstanding; it is red ONLY WHEN OVERDUE now — the row's
                      own `isOverduePF` (above, the rule the picker's "Overdue" pill
                      applies: InvoiceView.tsx's `isOverduePF` is the same predicate,
                      unpaid-ish AND past the due date, no grace), the badge beside
                      the channel chips reads the same call — and the row's own
                      colour otherwise, as the fees and total cells beside it. This
                      is the ONE table the Invoices section, CV and CCV draw
                      (158 §5), so the two colours land on all three. */}
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "6px",
                      textAlign: "right",
                      fontFamily: "'JetBrains Mono',monospace",
                      ...(dueColor ? { color: dueColor } : {}),
                    }}
                  >
                    {outstanding ? fc(remaining, iv.currency) : "—"}
                  </td>
                  {/* Phase 35 Stage-3 r2 (§3) — To Client / To Peppol channels
                      (payment state lives in the filters + the due figure). */}
                  <td style={{ padding: "6px", whiteSpace: "nowrap" }}>
                    {isCancelled ? (
                      <span style={{ fontSize: FS.s9, padding: "2px 6px", borderRadius: RAD.r10, background: "rgba(248,113,113,.12)", color: "var(--rd)", border: "1px solid rgba(248,113,113,.3)", fontWeight: 600 }}>
                        {t("invoice.status.cancelled", { fallback: "Cancelled" })}
                      </span>
                    ) : (
                      <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                        {/* 179 B.6 [RULED, ruling 1] — ONE chip before the To-Client /
                            To-Peppol pair, in their exact shape: grey "Approval" until
                            `approvedDate`, then purple "Approved" (the purple tint family
                            ProFormaModal's Restore button already uses). Purple is the
                            colour of the act; the sent-lock's icon leaves purple in C.6.
                            This cell is the ONE status cell for Invoices, CV and CCV. */}
                        <span
                          title={approvalWord}
                          style={{ fontSize: FS.s8_5, padding: "2px 5px", borderRadius: RAD.r10, fontWeight: 600, background: approved ? "rgba(167,139,250,.15)" : "rgba(120,120,120,.10)", color: approved ? "var(--pp)" : "var(--tm)", border: `1px solid ${approved ? "rgba(167,139,250,.3)" : "var(--br)"}` }}
                        >
                          {approvalWord}
                        </span>
                        {/* 179 B.6 — `as const` on the two tuples: with `t` typed, the
                            literal inferred `(string | boolean)[]` and `key`/`title` refused
                            it; a readonly tuple is what it always was at runtime. */}
                        {[
                          ["client", !!iv.sentToClientAt, t("invoice.col.to_client", { fallback: "To Client" })] as const,
                          ["peppol", !!iv.sentToPeppolAt, t("invoice.col.to_peppol", { fallback: "To Peppol" })] as const,
                        ].map(([k, sent, lbl]) => (
                          <span
                            key={k}
                            title={lbl}
                            style={{ fontSize: FS.s8_5, padding: "2px 5px", borderRadius: RAD.r10, fontWeight: 600, background: sent ? "rgba(52,211,153,.12)" : "rgba(120,120,120,.10)", color: sent ? "var(--gn)" : "var(--tm)", border: `1px solid ${sent ? "rgba(52,211,153,.3)" : "var(--br)"}` }}
                          >
                            {lbl}: {sent ? t("invoice.sent", { fallback: "Sent" }) : t("invoice.unsent", { fallback: "Unsent" })}
                          </span>
                        ))}
                        {/* 191 E.2d — the "En retard" badge that stood here is
                            gone: the red DÛ already says it ([BB]). */}
                      </span>
                    )}
                  </td>
                  <td
                    style={{ padding: "6px", whiteSpace: "nowrap" }}
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    {/* 216 §3 [RULED 72] — the window opens on any pro forma
                        that carries a payment too (a paid one, to take one
                        back): the button stays while something is due, and
                        stays once paid if a payment is recorded. */}
                    {(iv.paymentStatus === "unpaid" ||
                      iv.paymentStatus === "partially_paid" ||
                      carriesPayment) && (
                      <button
                        className="btn"
                        data-payment-window={iv.id}
                        style={{
                          padding: "2px 6px",
                          fontSize: FS.s9,
                          background: "rgba(52,211,153,.12)",
                          color: "var(--gn)",
                          border: "1px solid rgba(52,211,153,.3)",
                        }}
                        onClick={() => {
                          // 179 C.8 — the pre-fill used to be browser arithmetic
                          // (totalToPay − Σ payments(JSON)); the server serves
                          // `amountDue` as THE authority for what the client still
                          // owes (shapes.js structuralShapeInvoice, credits and
                          // applied provisions included). Read that. C.2's class.
                          // 216 addendum 3 §3′ (2) — through the one question
                          // (dueStateOf): the due rounded to the cent, its sign
                          // kept for a reimbursement; the `> 0 ? due : total`
                          // fork that stood here pre-filled a residue « 0,00 ».
                          setPayMod({
                            invoiceId: iv.id,
                            amount: dueStateOf(iv as DueDoc).prefill,
                            date: new Date().toISOString().slice(0, 10),
                            reference: "",
                          });
                        }}
                      >
                        <BanknoteArrowDown
                          size={14}
                          style={{
                            marginRight: 3,
                            verticalAlign: "middle",
                          }}
                        />{" "}
                        {t("invoice.btn.payment")}
                      </button>
                    )}
                    {/* Phase 35 Stage-3 — the "+ Credit" row action is
                      removed; commercial deduction is set in the create
                      modal (replaces the credits modal). */}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </HScroll>
      </div>
      {payMod &&
        (() => {
          const target = (inv || []).find((i: any) => i.id === payMod.invoiceId);
          if (!target) return null;
          const tp = (target.payments || []).reduce((a: any, p: any) => a + p.amount, 0);
          // Phase 35 (C3) — credits reduce the effective amount due (shown).
          const credited = sumCredits(target);
          // 216 addendum 3 §3′ (2) — the served due and the list's rule, once
          // (dueStateOf): `rem` drives « Dû » and its colour, `owed` the
          // recording part and its button. `totalToPay − credited − tp` with a
          // `> 0` stood here, three times.
          const { due: rem, owed } = dueStateOf(target as DueDoc);
          // 216 addendum 3 §3′ (3) — the window's own flags, read once through their type (the ratchet)
          const win = payMod as { removed?: boolean; fill?: number };
          const leaves = win.removed || !owed;
          return (
            <ModalLayout
              title={t("invoice.payment.title", { number: target.number })}
              onClose={() => setPayMod(null)}
              showX={false}
              maxWidth={380}
              footer={
                <div className="modal-footer">
                  {/* 216 addendum 3 §3′ (3) [RULED 78] — THE BUTTON THAT LEAVES
                      reads « Fermer » once a payment has been removed in this
                      window (Laurent's choice, 12/10: leaving by « Annuler »
                      after a removal read as undoing it), and [ADVISOR] whenever
                      nothing is due — there is nothing to cancel on a paid
                      document; « Annuler », as before, otherwise. Both existing
                      keys; the button's class and act are unchanged. */}
                  <button
                    type="button"
                    className="modal-cancel"
                    data-payment-leave={leaves ? "close" : "cancel"}
                    onClick={() => setPayMod(null)}
                  >
                    {leaves ? t("common.close") : t("common.cancel")}
                  </button>
                  {/* 216 §3 [ADVISOR] — the recording part shows only while
                      something is due; on a paid pro forma the window is for
                      taking a payment back. 216 addendum 3 §3′ (2) — « due »
                      as the list asks it (dueStateOf), a reimbursement by its
                      sign: its repayment can be recorded here again. */}
                  {owed && (
                    <button type="button" className="modal-save" onClick={recordPayment}>
                      {t("invoice.payment.record")}
                    </button>
                  )}
                </div>
              }
            >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: FS.s11,
                      marginBottom: 10,
                      padding: "6px 10px",
                      background: "rgba(255,255,255,.03)",
                      borderRadius: RAD.r6,
                      border: "1px solid var(--br)",
                    }}
                  >
                    <span>
                      {t("invoice.payment.total")}:{" "}
                      <strong>{fc(target.totalToPay, target.currency)}</strong>
                    </span>
                    {credited > 0 && (
                      <span>
                        {t("invoice.credit.credited", { fallback: "Credited" })}:{" "}
                        <strong style={{ color: "var(--am)" }}>
                          {fc(credited, target.currency)}
                        </strong>
                      </span>
                    )}
                    <span>
                      {t("invoice.payment.paid")}:{" "}
                      <strong style={{ color: "var(--gn)" }}>{fc(tp, target.currency)}</strong>
                    </span>
                    <span>
                      {t("invoice.payment.due")}:{" "}
                      <strong
                        style={{ color: owed ? "var(--rd)" : "var(--gn)" }}
                      >
                        {fc(rem, target.currency)}
                      </strong>
                    </span>
                  </div>
                  {(target.payments || []).length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          fontSize: FS.s9,
                          color: "var(--tm)",
                          textTransform: "uppercase",
                          marginBottom: 4,
                        }}
                      >
                        {t("invoice.payment.previous")}
                      </div>
                      {(target.payments || []).map((p: any, i: any) => {
                        // 216 §3 (2) [ADVISOR] — the documents drawing on this
                        // payment (shapes.js `drawnBy`, served with the row id):
                        // the × stays, and a click names them with the amount
                        // each drew instead of removing — the server refuses
                        // the same, under the same sentence (its code's row),
                        // so one key says it on both planes.
                        const drawnBy = (p as { drawnBy?: { id: number | null; number: string | null; amount: number }[] }).drawnBy;
                        const drawn = Array.isArray(drawnBy) && drawnBy.length > 0;
                        const cur = (target as ServedInvoice).currency ?? null;
                        const drawnList = drawn
                          ? drawnBy.map((d) => { const name = d.number ?? (d.id == null ? "?" : "#" + String(d.id)); return `${name} (${fc(d.amount, cur)})`; }).join(", ")
                          : "";
                        const drawnWord = t("srvErr.invoice.payment_drawn", {
                          list: drawnList,
                          fallback: `This payment is used by ${drawnList}: release it there first.`,
                        });
                        return (
                        <div
                          key={p.id ?? i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: FS.s10,
                            padding: "2px 0",
                            borderBottom: "1px solid rgba(255,255,255,.04)",
                          }}
                        >
                          {/* 216 addendum 3 §3′ (6) [ADVISOR] — the row prints the
                              document's currency, as the confirm's title does
                              (169 R8); `fq(amount) €` stood here — a euro row
                              prints exactly as before. */}
                          <span style={{ color: "var(--gn)", minWidth: 70 }}>
                            {fc(p.amount, cur)}
                          </span>
                          <span style={{ color: "var(--tm)" }}>
                            {fd(p.date)}
                          </span>
                          <span style={{ color: "var(--ts)", flex: 1 }}>
                            {p.reference}
                          </span>
                          {/* 160 §1 — `p.id` exists only when the row store
                              served one (156 §4.2: a figure that cannot be
                              computed is absent, never zero). 216 §3 [RULED
                              72] — the second condition that stood here, « not
                              sent », is gone: every payment's × shows, sent or
                              not, paid in full or not; the server accepts the
                              same. */}
                          {p.id != null && (
                            <button
                              type="button"
                              className="btn btn-danger"
                              data-payment-remove={drawn ? "drawn" : "free"}
                              style={{ padding: "0 5px", fontSize: FS.s9, lineHeight: 1.6 }}
                              title={drawn ? drawnWord : t("invoice.payment.remove_tip", {
                                fallback: "Remove this payment.",
                              })}
                              onClick={() => { if (drawn) say(`⚠ ${drawnWord}`, "error"); else removePayment(target, p, Number(i)); }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                  {owed && (<>
                  <div className="field">
                    {/* 169 R6/R8 — the dialog names the document's currency; the
                        old key stands on a firm-currency document. */}
                    <label>
                      {target.currency && target.currency !== firmCurrencyOf()
                        ? t("invoice.payment.amount_label_cur", { currency: target.currency, fallback: `Amount (${target.currency}) *` })
                        : t("invoice.payment.amount_label")}
                    </label>
                    {/* 208 §10 — the one amount field (src/components/AmountField.tsx): typed with a decimal comma, shown « 1 234,50 »
                        216 addendum 6 §10 (4) [RULED 82] — a leading minus where the document is a reimbursement, and only there */}
                    <AmountField
                      key={win.fill ?? 0}
                      value={payMod.amount}
                      empty=""
                      inputMode="decimal"
                      negative={(target as { docKind?: string | null }).docKind === "credit"}
                      onAmount={(n) =>
                        setPayMod((p: any) => ({ ...p, amount: n == null ? "" : String(n) }))
                      }
                      autoFocus
                    />
                  </div>
                  <div className="field-row">
                    <div className="field">
                      <label>{t("invoice.te.col.date")}</label>
                      <input
                        className="field-input"
                        type="date"
                        value={payMod.date}
                        onChange={(e) =>
                          setPayMod((p: any) => ({ ...p, date: e.target.value }))
                        }
                      />
                    </div>
                    <div className="field">
                      <label>{t("invoice.payment.reference")}</label>
                      <input
                        className="field-input"
                        value={payMod.reference}
                        onChange={(e) =>
                          setPayMod((p: any) => ({
                            ...p,
                            reference: e.target.value,
                          }))
                        }
                        placeholder={t("invoice.payment.reference_placeholder")}
                      />
                    </div>
                  </div>
                  </>)}
              </ModalLayout>
          );
        })()}
    </>
  );
}
